import type { Meal, MealIngredient } from '../domain/types.js';

/** Terse constructor so 60 meals stay readable. */
const item = (
  ingredientId: string,
  qtyPerPortion: number,
  unit: MealIngredient['unit'],
  prepNote: string | null = null,
  isOptional = false,
): MealIngredient => ({ ingredientId, qtyPerPortion, unit, isOptional, prepNote });

export const MEALS: Meal[] = [
  {
    id: 'spaghetti-bolognese',
    name: 'Spaghetti bolognese',
    description: 'The weeknight standard. Doubles happily and freezes well.',
    cuisine: 'italian',
    mainProtein: 'beef',
    effortMinutes: 45,
    costBand: 'budget',
    tags: ['budget', 'batch', 'mild', 'freezer'],
    ingredients: [
      item('beef-mince', 110, 'g'),
      item('pasta-spaghetti', 85, 'g'),
      item('chopped-tomatoes', 120, 'g'),
      item('onions', 0.4, 'each', 'finely diced'),
      item('garlic', 0.7, 'each', 'crushed'),
      item('carrots', 35, 'g', 'grated'),
      item('tomato-puree', 8, 'g'),
      item('stock-cubes-beef', 0.3, 'each'),
      item('olive-oil', 5, 'ml'),
      item('mixed-herbs', 1, 'g'),
      item('fresh-basil', 2, 'g', 'to finish', true),
    ],
    method: [
      'Soften the onion, carrot and garlic in the oil.',
      'Brown the mince, breaking it up as it colours.',
      'Add the tomatoes, puree, crumbled stock cube and herbs.',
      'Simmer 25 minutes while the spaghetti cooks.',
      'Season, and finish with basil if using.',
    ],
  },
  {
    id: 'chilli-con-carne',
    name: 'Chilli con carne',
    description: 'A big batch pot that only gets better the next day.',
    cuisine: 'mexican',
    mainProtein: 'beef',
    effortMinutes: 50,
    costBand: 'budget',
    tags: ['budget', 'batch', 'mild', 'freezer'],
    ingredients: [
      item('beef-mince', 120, 'g'),
      item('kidney-beans', 90, 'g'),
      item('chopped-tomatoes', 120, 'g'),
      item('onions', 0.4, 'each', 'diced'),
      item('garlic', 0.7, 'each', 'crushed'),
      item('peppers', 0.4, 'each', 'diced'),
      item('chilli-powder', 1.5, 'g'),
      item('cumin', 1, 'g'),
      item('tomato-puree', 8, 'g'),
      item('stock-cubes-beef', 0.3, 'each'),
      item('rice-long-grain', 80, 'g'),
      item('fresh-coriander', 2, 'g', 'to finish', true),
    ],
    method: [
      'Soften the onion, pepper and garlic.',
      'Brown the mince.',
      'Stir in the tomatoes, puree, crumbled stock cube, chilli and cumin.',
      'Add the kidney beans and simmer 30 minutes.',
      'Cook the rice.',
      'Serve the chilli over rice, coriander to finish.',
    ],
  },
  {
    id: 'cottage-pie',
    name: 'Cottage pie',
    description: 'Mince topped with buttery mash, the whole family finishes their plate.',
    cuisine: 'british',
    mainProtein: 'beef',
    effortMinutes: 60,
    costBand: 'budget',
    tags: ['budget', 'batch', 'mild', 'freezer'],
    ingredients: [
      item('beef-mince', 120, 'g'),
      item('potatoes', 180, 'g', 'for mash'),
      item('carrots', 35, 'g', 'diced'),
      item('onions', 0.4, 'each', 'diced'),
      item('garlic', 0.5, 'each', 'crushed'),
      item('tomato-puree', 8, 'g'),
      item('worcestershire-sauce', 4, 'ml'),
      item('stock-cubes-beef', 0.3, 'each'),
      item('butter', 10, 'g'),
      item('milk', 30, 'ml'),
      item('cheddar', 15, 'g', 'to top, grated', true),
    ],
    method: [
      'Boil the potatoes until soft, then mash with butter and milk.',
      'Soften the onion, carrot and garlic.',
      'Brown the mince, then stir in puree, worcestershire sauce and crumbled stock cube.',
      'Simmer 20 minutes until thickened.',
      'Top with the mash, and cheddar if using.',
      'Bake or grill until the top colours.',
    ],
  },
  {
    id: 'shepherds-pie',
    name: "Shepherd's pie",
    description: 'Cottage pie’s lamb cousin, just as freezer-friendly.',
    cuisine: 'british',
    mainProtein: 'lamb',
    effortMinutes: 60,
    costBand: 'mid',
    tags: ['batch', 'mild', 'freezer'],
    ingredients: [
      item('lamb-mince', 120, 'g'),
      item('potatoes', 180, 'g', 'for mash'),
      item('carrots', 35, 'g', 'diced'),
      item('onions', 0.4, 'each', 'diced'),
      item('garlic', 0.5, 'each', 'crushed'),
      item('tomato-puree', 8, 'g'),
      item('worcestershire-sauce', 4, 'ml'),
      item('stock-cubes-beef', 0.3, 'each'),
      item('butter', 10, 'g'),
      item('milk', 30, 'ml'),
    ],
    method: [
      'Boil the potatoes until soft, then mash with butter and milk.',
      'Soften the onion, carrot and garlic.',
      'Brown the lamb, then stir in puree, worcestershire sauce and crumbled stock cube.',
      'Simmer 20 minutes until thickened.',
      'Top with the mash and rough up the surface with a fork.',
      'Bake until the top is golden.',
    ],
  },
  {
    id: 'chicken-korma',
    name: 'Chicken korma',
    description: 'A mild, creamy curry the whole table can eat.',
    cuisine: 'indian',
    mainProtein: 'chicken',
    effortMinutes: 40,
    costBand: 'mid',
    tags: ['mild'],
    ingredients: [
      item('chicken-breast', 140, 'g', 'diced'),
      item('curry-paste-korma', 30, 'g'),
      item('coconut-milk', 60, 'ml'),
      item('onions', 0.4, 'each', 'diced'),
      item('garlic', 0.6, 'each', 'crushed'),
      item('ginger', 4, 'g', 'grated'),
      item('rice-basmati', 80, 'g'),
      item('fresh-coriander', 2, 'g', 'to finish', true),
    ],
    method: [
      'Soften the onion, garlic and ginger.',
      'Add the chicken and brown lightly.',
      'Stir in the korma paste, then the coconut milk.',
      'Simmer 20 minutes until the chicken is cooked through.',
      'Cook the rice.',
      'Serve with rice, coriander to finish.',
    ],
  },
  {
    id: 'chicken-tikka-masala',
    name: 'Chicken tikka masala',
    description: 'A weekend curry night favourite, gently spiced.',
    cuisine: 'indian',
    mainProtein: 'chicken',
    effortMinutes: 45,
    costBand: 'mid',
    tags: ['mild'],
    ingredients: [
      item('chicken-breast', 150, 'g', 'diced'),
      item('curry-powder', 3, 'g'),
      item('smoked-paprika', 2, 'g'),
      item('chopped-tomatoes', 120, 'g'),
      item('double-cream', 40, 'ml'),
      item('onions', 0.4, 'each', 'diced'),
      item('garlic', 0.7, 'each', 'crushed'),
      item('ginger', 4, 'g', 'grated'),
      item('rice-basmati', 80, 'g'),
    ],
    method: [
      'Brown the chicken with the curry powder and paprika.',
      'Soften the onion, garlic and ginger.',
      'Add the tomatoes and simmer 15 minutes.',
      'Return the chicken, stir in the cream, and simmer until cooked through.',
      'Cook the rice.',
      'Serve together.',
    ],
  },
  {
    id: 'beef-stew-dumplings',
    name: 'Beef stew & dumplings',
    description: 'A slow Sunday pot, worth the wait.',
    cuisine: 'british',
    mainProtein: 'beef',
    effortMinutes: 90,
    costBand: 'budget',
    tags: ['budget', 'batch', 'mild'],
    ingredients: [
      item('diced-beef', 128, 'g'),
      item('carrots', 40, 'g', 'chopped'),
      item('onions', 0.4, 'each', 'diced'),
      item('celery', 0.5, 'each', 'sliced'),
      item('potatoes', 100, 'g', 'chunked'),
      item('stock-cubes-beef', 0.4, 'each'),
      item('plain-flour', 30, 'g', 'covers both thickening the stew and forming the dumplings'),
      item('butter', 8, 'g', 'dumplings'),
      item('mixed-herbs', 1, 'g'),
    ],
    method: [
      'Brown the beef in batches.',
      'Soften the onion, carrot and celery.',
      'Return the beef, add stock, potatoes and herbs, and simmer 1.5 hours.',
      'Mix flour and butter with a little water into dumplings.',
      'Drop onto the stew for the last 20 minutes to cook through.',
      'Season and serve.',
    ],
  },
  {
    id: 'sausage-mash',
    name: 'Sausage & mash with onion gravy',
    description: 'A fast, filling standby for the hardest weeknights.',
    cuisine: 'british',
    mainProtein: 'pork',
    effortMinutes: 30,
    costBand: 'budget',
    tags: ['budget', 'quick', 'mild'],
    ingredients: [
      item('sausages', 2, 'each'),
      item('potatoes', 170, 'g'),
      item('onions', 0.4, 'each', 'sliced'),
      item('gravy-granules', 10, 'g'),
      item('butter', 10, 'g'),
      item('milk', 30, 'ml'),
    ],
    method: [
      'Grill or fry the sausages until browned through.',
      'Boil the potatoes, then mash with butter and milk.',
      'Soften the onion for the gravy, then stir in gravy granules and water.',
      'Serve the sausages on mash with onion gravy.',
    ],
  },
  {
    id: 'toad-in-the-hole',
    name: 'Toad in the hole',
    description: 'Sausages baked into a crisp Yorkshire batter.',
    cuisine: 'british',
    mainProtein: 'pork',
    effortMinutes: 45,
    costBand: 'budget',
    tags: ['budget', 'mild'],
    ingredients: [
      item('sausages', 2, 'each'),
      item('plain-flour', 40, 'g'),
      item('eggs', 0.5, 'each'),
      item('milk', 60, 'ml'),
      item('vegetable-oil', 8, 'ml'),
      item('onions', 0.3, 'each', 'sliced, for gravy'),
      item('gravy-granules', 10, 'g'),
    ],
    method: [
      'Heat the oil in a tin in the oven until smoking.',
      'Whisk the flour, egg and milk into a batter.',
      'Add the sausages to the hot tin, then pour over the batter.',
      'Bake until risen and golden, about 30 minutes.',
      'Make onion gravy on the hob while it bakes.',
      'Serve immediately.',
    ],
  },
  {
    id: 'roast-chicken-dinner',
    name: 'Roast chicken dinner',
    description: 'The Sunday classic, with gravy from the resting juices.',
    cuisine: 'british',
    mainProtein: 'chicken',
    effortMinutes: 90,
    costBand: 'mid',
    tags: ['mild'],
    ingredients: [
      item('whole-chicken', 0.3, 'each'),
      item('potatoes', 200, 'g', 'for roasting'),
      item('carrots', 40, 'g'),
      item('green-beans', 60, 'g'),
      item('gravy-granules', 12, 'g'),
      item('olive-oil', 8, 'ml'),
    ],
    method: [
      'Roast the chicken with oil until cooked through, basting occasionally.',
      'Parboil then roast the potatoes alongside.',
      'Steam the carrots and green beans.',
      'Make gravy from the resting juices and granules.',
      'Carve and serve with the vegetables and gravy.',
    ],
  },
  {
    id: 'fish-pie',
    name: 'Fish pie',
    description: 'A creamy mash-topped bake that reheats beautifully.',
    cuisine: 'british',
    mainProtein: 'fish',
    effortMinutes: 55,
    costBand: 'mid',
    tags: ['batch', 'mild'],
    ingredients: [
      item('smoked-haddock', 96, 'g'),
      item('prawns', 60, 'g'),
      item('potatoes', 180, 'g', 'for mash'),
      item('milk', 60, 'ml'),
      item('butter', 12, 'g'),
      item('plain-flour', 8, 'g'),
      item('peas-frozen', 50, 'g'),
      item('cheddar', 15, 'g', 'to top', true),
    ],
    method: [
      'Poach the haddock gently in the milk.',
      'Boil and mash the potatoes with butter.',
      'Make a white sauce with the poaching milk and flour.',
      'Flake the fish into the sauce with the peas and prawns.',
      'Top with the mash and cheddar if using.',
      'Bake until golden and bubbling.',
    ],
  },
  {
    id: 'breaded-fish-chips',
    name: 'Breaded fish, chips & peas',
    description: 'A quick chip-shop night at home.',
    cuisine: 'british',
    mainProtein: 'fish',
    effortMinutes: 30,
    costBand: 'budget',
    tags: ['budget', 'quick', 'mild'],
    ingredients: [
      item('white-fish-fillets', 1, 'each'),
      item('breadcrumbs', 30, 'g'),
      item('eggs', 0.3, 'each', 'for coating'),
      item('plain-flour', 10, 'g', 'for coating'),
      item('chips-frozen', 150, 'g'),
      item('peas-frozen', 60, 'g'),
    ],
    method: [
      'Dip the fish in flour, then egg, then breadcrumbs.',
      'Bake or fry the chips.',
      'Fry or bake the breaded fish until golden and cooked through.',
      'Boil the peas.',
      'Serve together.',
    ],
  },
  {
    id: 'tuna-pasta-bake',
    name: 'Tuna pasta bake',
    description: 'A storecupboard bake that comes together from tins.',
    cuisine: 'italian',
    mainProtein: 'fish',
    effortMinutes: 30,
    costBand: 'budget',
    tags: ['budget', 'quick', 'batch', 'mild'],
    ingredients: [
      item('tuna-tinned', 100, 'g'),
      item('pasta-fusilli', 85, 'g'),
      item('sweetcorn-tinned', 40, 'g'),
      item('chopped-tomatoes', 100, 'g'),
      item('cheddar', 25, 'g'),
      item('mixed-herbs', 1, 'g'),
    ],
    method: [
      'Cook the pasta.',
      'Warm the tomatoes with the herbs.',
      'Stir in the drained tuna and sweetcorn.',
      'Combine with the cooked pasta and top with cheddar.',
      'Finish under the grill for a few minutes until the cheese is golden.',
    ],
  },
  {
    id: 'mac-and-cheese',
    name: 'Mac and cheese',
    description: 'A cheese-sauce pasta every child in the house eats without a fuss.',
    cuisine: 'american',
    mainProtein: 'none',
    effortMinutes: 30,
    costBand: 'budget',
    tags: ['budget', 'quick', 'veggie', 'mild'],
    ingredients: [
      item('pasta-fusilli', 85, 'g'),
      item('cheddar', 40, 'g'),
      item('milk', 80, 'ml'),
      item('butter', 12, 'g'),
      item('plain-flour', 10, 'g'),
      item('mustard', 3, 'g', 'to sharpen the sauce', true),
    ],
    method: [
      'Cook the pasta.',
      'Melt the butter, whisk in the flour, then the milk to make a white sauce.',
      'Stir in most of the cheddar until melted, and the mustard if using.',
      'Combine with the pasta.',
      'Top with the remaining cheddar and grill until golden.',
    ],
  },
  {
    id: 'margherita-pizza',
    name: 'Homemade margherita pizza',
    description: 'A dough-from-scratch pizza night, toppings kept simple for little ones.',
    cuisine: 'italian',
    mainProtein: 'none',
    effortMinutes: 40,
    costBand: 'budget',
    tags: ['budget', 'veggie', 'mild'],
    ingredients: [
      item('plain-flour', 90, 'g'),
      item('dried-yeast', 2, 'g'),
      item('olive-oil', 6, 'ml'),
      item('passata', 60, 'g'),
      item('mozzarella', 40, 'g'),
      item('fresh-basil', 2, 'g', 'to finish', true),
    ],
    method: [
      'Mix the flour, yeast, oil and water into a dough and knead.',
      'Prove until doubled, about 20 minutes.',
      'Roll out and spread with passata.',
      'Top with mozzarella.',
      'Bake at the highest oven setting until crisp and golden.',
      'Scatter with basil if using.',
    ],
  },
  {
    id: 'chicken-fajitas',
    name: 'Chicken fajitas',
    description: 'Build-your-own wraps, sliced small for the twins.',
    cuisine: 'mexican',
    mainProtein: 'chicken',
    effortMinutes: 30,
    costBand: 'mid',
    tags: ['quick'],
    ingredients: [
      item('chicken-breast', 140, 'g', 'sliced'),
      item('peppers', 0.5, 'each', 'sliced'),
      item('onions', 0.4, 'each', 'sliced'),
      item('wraps', 1.5, 'each'),
      item('smoked-paprika', 2, 'g'),
      item('cumin', 1.5, 'g'),
      item('limes', 0.3, 'each'),
      item('greek-yoghurt', 20, 'g', 'to serve', true),
    ],
    method: [
      'Slice the chicken and toss with the paprika and cumin.',
      'Fry the chicken until cooked through.',
      'Add the peppers and onion, and fry until softened.',
      'Warm the wraps.',
      'Serve with a squeeze of lime and yoghurt if using.',
    ],
  },
  {
    id: 'beef-tacos',
    name: 'Beef tacos',
    description: 'A build-at-the-table dinner, twenty-five minutes start to finish.',
    cuisine: 'mexican',
    mainProtein: 'beef',
    effortMinutes: 25,
    costBand: 'mid',
    tags: ['quick', 'mild'],
    ingredients: [
      item('beef-mince', 110, 'g'),
      item('wraps', 1.5, 'each'),
      item('fresh-tomatoes', 40, 'g', 'diced'),
      item('cheddar', 25, 'g', 'grated'),
      item('lettuce', 0.15, 'each', 'shredded'),
      item('cumin', 1.5, 'g'),
      item('smoked-paprika', 1.5, 'g'),
    ],
    method: [
      'Brown the mince with the cumin and paprika.',
      'Warm the wraps.',
      'Fill with the mince, tomato, lettuce and cheddar.',
      'Serve to build at the table.',
    ],
  },
  {
    id: 'sweet-sour-chicken',
    name: 'Sweet & sour chicken',
    description: 'A jarred-sauce shortcut that still feels like a treat.',
    cuisine: 'chinese',
    mainProtein: 'chicken',
    effortMinutes: 35,
    costBand: 'mid',
    tags: ['mild'],
    ingredients: [
      item('chicken-breast', 140, 'g', 'diced'),
      item('sweet-sour-sauce', 90, 'g'),
      item('peppers', 0.4, 'each', 'diced'),
      item('onions', 0.3, 'each', 'diced'),
      item('rice-long-grain', 80, 'g'),
    ],
    method: [
      'Fry the chicken until golden and cooked through.',
      'Add the peppers and onion, and fry until just softened.',
      'Stir in the sweet and sour sauce and simmer 5 minutes.',
      'Cook the rice.',
      'Serve together.',
    ],
  },
  {
    id: 'chicken-chow-mein',
    name: 'Chicken chow mein',
    description: 'A twenty-five-minute stir-fry for the busiest nights.',
    cuisine: 'chinese',
    mainProtein: 'chicken',
    effortMinutes: 25,
    costBand: 'mid',
    tags: ['quick'],
    ingredients: [
      item('chicken-breast', 130, 'g', 'sliced'),
      item('noodles-egg', 85, 'g'),
      item('peppers', 0.3, 'each', 'sliced'),
      item('spring-onions', 1, 'each', 'sliced'),
      item('cabbage', 0.15, 'each', 'shredded'),
      item('soy-sauce', 12, 'ml'),
      item('ginger', 3, 'g', 'grated'),
    ],
    method: [
      'Cook the noodles and drain.',
      'Stir-fry the chicken until cooked through.',
      'Add the peppers, cabbage and ginger, and stir-fry briefly.',
      'Toss through the noodles and soy sauce.',
      'Scatter with spring onion.',
    ],
  },
  {
    id: 'egg-fried-rice-prawns',
    name: 'Egg fried rice with prawns',
    description: 'A twenty-minute plate built on leftover rice.',
    cuisine: 'chinese',
    mainProtein: 'prawns',
    effortMinutes: 20,
    costBand: 'mid',
    tags: ['quick'],
    ingredients: [
      item('prawns', 90, 'g'),
      item('rice-long-grain', 85, 'g', 'cooked and cooled'),
      item('eggs', 0.7, 'each', 'beaten'),
      item('peas-frozen', 40, 'g'),
      item('spring-onions', 1, 'each', 'sliced'),
      item('soy-sauce', 10, 'ml'),
    ],
    method: [
      'Cook the rice ahead and cool, or use leftover rice.',
      'Scramble the egg in a hot wok, then set aside.',
      'Stir-fry the prawns and peas.',
      'Add the rice, egg and soy sauce, and toss until hot through.',
      'Scatter with spring onion.',
    ],
  },
  {
    id: 'thai-green-curry',
    name: 'Thai green curry',
    description: 'A fragrant paste-based curry, ready in well under an hour.',
    cuisine: 'thai',
    mainProtein: 'chicken',
    effortMinutes: 35,
    costBand: 'mid',
    // Not 'mild': thai green curry paste carries real heat. Not 'quick':
    // effortMinutes is 35, over the 30-minute cutoff.
    tags: ['batch'],
    ingredients: [
      item('chicken-breast', 140, 'g', 'sliced'),
      item('curry-paste-thai-green', 30, 'g'),
      item('coconut-milk', 90, 'ml'),
      item('peppers', 0.4, 'each', 'sliced'),
      item('green-beans', 50, 'g'),
      item('rice-basmati', 80, 'g'),
      item('fresh-coriander', 2, 'g', 'to finish', true),
    ],
    method: [
      'Fry the curry paste briefly to release the aroma.',
      'Add the chicken and brown lightly.',
      'Stir in the coconut milk, peppers and beans.',
      'Simmer 15 minutes until the chicken is cooked through.',
      'Cook the rice.',
      'Serve with coriander to finish.',
    ],
  },
  {
    id: 'katsu-curry',
    name: 'Chicken katsu curry',
    description: 'Crumbed chicken over rice with a mild, sweet sauce.',
    cuisine: 'japanese',
    mainProtein: 'chicken',
    effortMinutes: 45,
    costBand: 'mid',
    tags: ['mild'],
    ingredients: [
      item('chicken-breast', 150, 'g'),
      item('breadcrumbs', 35, 'g'),
      item('eggs', 0.3, 'each'),
      item('plain-flour', 10, 'g'),
      item('katsu-sauce', 74, 'g'),
      item('rice-basmati', 80, 'g'),
      item('cabbage', 0.15, 'each', 'shredded', true),
    ],
    method: [
      'Flatten the chicken breast, then coat in flour, egg and breadcrumbs.',
      'Fry or bake until golden and cooked through.',
      'Warm the katsu sauce.',
      'Cook the rice.',
      'Slice the chicken and serve over rice with the sauce, cabbage on the side if using.',
    ],
  },
  {
    id: 'lasagne',
    name: 'Lasagne',
    description: 'The layered oven bake worth the extra time.',
    cuisine: 'italian',
    mainProtein: 'beef',
    effortMinutes: 70,
    costBand: 'mid',
    tags: ['batch', 'mild', 'freezer'],
    ingredients: [
      item('beef-mince', 120, 'g'),
      item('lasagne-sheets', 70, 'g'),
      item('chopped-tomatoes', 120, 'g'),
      item('onions', 0.4, 'each', 'diced'),
      item('garlic', 0.7, 'each', 'crushed'),
      item('carrots', 30, 'g', 'grated'),
      item('tomato-puree', 8, 'g'),
      item('milk', 60, 'ml'),
      item('plain-flour', 10, 'g'),
      item('butter', 10, 'g'),
      item('cheddar', 25, 'g'),
      item('mixed-herbs', 1, 'g'),
    ],
    method: [
      'Soften the onion, carrot and garlic, then brown the mince.',
      'Add the tomatoes, puree and herbs, and simmer 20 minutes.',
      'Make a white sauce with the butter, flour and milk.',
      'Layer mince, lasagne sheets and white sauce in a dish, finishing with sauce and cheddar.',
      'Bake until golden and the pasta is tender, about 35 minutes.',
    ],
  },
  {
    id: 'carbonara',
    name: 'Spaghetti carbonara',
    description: 'A twenty-minute pan sauce, no cream required.',
    cuisine: 'italian',
    mainProtein: 'pork',
    effortMinutes: 20,
    costBand: 'budget',
    tags: ['budget', 'quick', 'mild'],
    ingredients: [
      item('pasta-spaghetti', 90, 'g'),
      item('bacon', 45, 'g', 'diced'),
      item('eggs', 1, 'each'),
      item('parmesan', 20, 'g', 'grated'),
      item('black-pepper', 1, 'g'),
    ],
    method: [
      'Cook the spaghetti.',
      'Fry the bacon until crisp.',
      'Whisk the eggs with the parmesan and black pepper.',
      'Off the heat, toss the drained pasta with the bacon, then the egg mixture, so it thickens without scrambling.',
      'Serve immediately with extra parmesan.',
    ],
  },
  {
    id: 'pesto-pasta-chicken',
    name: 'Pesto pasta with chicken',
    description: 'A jar-of-pesto dinner that still tastes like effort.',
    cuisine: 'italian',
    mainProtein: 'chicken',
    effortMinutes: 20,
    costBand: 'mid',
    tags: ['quick', 'mild'],
    ingredients: [
      item('pasta-fusilli', 85, 'g'),
      item('chicken-breast', 130, 'g', 'diced'),
      item('pesto', 25, 'g'),
      item('fresh-tomatoes', 40, 'g', 'halved'),
      item('parmesan', 15, 'g', 'to serve', true),
    ],
    method: [
      'Cook the pasta.',
      'Fry the chicken until cooked through.',
      'Toss the drained pasta with the pesto, chicken and tomatoes.',
      'Serve with parmesan if using.',
    ],
  },
  {
    id: 'tomato-mascarpone-pasta',
    name: 'Tomato & mascarpone pasta',
    description: 'A creamy vegetarian pasta from four storecupboard staples.',
    cuisine: 'italian',
    mainProtein: 'none',
    effortMinutes: 20,
    costBand: 'budget',
    tags: ['budget', 'quick', 'veggie', 'mild'],
    ingredients: [
      item('pasta-penne', 90, 'g'),
      item('passata', 120, 'g'),
      item('mascarpone', 40, 'g'),
      item('garlic', 0.5, 'each', 'crushed'),
      item('fresh-basil', 2, 'g', 'to finish', true),
    ],
    method: [
      'Cook the pasta.',
      'Warm the passata with the garlic.',
      'Stir in the mascarpone until melted through.',
      'Toss with the drained pasta.',
      'Scatter with basil if using.',
    ],
  },
  {
    id: 'sausage-pasta-bake',
    name: 'Sausage pasta bake',
    description: 'Sausage meat in a tomato sauce, baked under melted cheese.',
    cuisine: 'italian',
    mainProtein: 'pork',
    effortMinutes: 40,
    costBand: 'budget',
    tags: ['budget', 'batch', 'mild', 'freezer'],
    ingredients: [
      item('sausages', 2, 'each'),
      item('pasta-fusilli', 85, 'g'),
      item('chopped-tomatoes', 120, 'g'),
      item('onions', 0.4, 'each', 'diced'),
      item('garlic', 0.6, 'each', 'crushed'),
      item('peppers', 0.3, 'each', 'diced'),
      item('mozzarella', 30, 'g'),
    ],
    method: [
      'Cook the pasta.',
      'Squeeze the sausage meat from its skin and brown, breaking it up.',
      'Soften the onion, garlic and pepper, then add the tomatoes and simmer 15 minutes.',
      'Combine with the pasta and top with mozzarella.',
      'Bake until golden and bubbling.',
    ],
  },
  {
    id: 'meatballs-tomato-sauce',
    name: 'Meatballs in tomato sauce',
    description: 'Homemade meatballs simmered in a simple tomato sauce.',
    cuisine: 'italian',
    mainProtein: 'beef',
    effortMinutes: 40,
    costBand: 'mid',
    tags: ['batch', 'mild', 'freezer'],
    ingredients: [
      item('beef-mince', 120, 'g'),
      item('breadcrumbs', 15, 'g'),
      item('eggs', 0.2, 'each'),
      item('chopped-tomatoes', 120, 'g'),
      item('onions', 0.4, 'each', 'diced'),
      item('garlic', 0.6, 'each', 'crushed'),
      item('tomato-puree', 8, 'g'),
      item('pasta-spaghetti', 85, 'g'),
      item('parmesan', 15, 'g', 'to serve', true),
    ],
    method: [
      'Mix the mince with the breadcrumbs and egg, and roll into balls.',
      'Brown the meatballs, then set aside.',
      'Soften the onion and garlic, add the tomatoes and puree, and simmer 10 minutes.',
      'Return the meatballs and simmer until cooked through, about 15 minutes.',
      'Cook the spaghetti.',
      'Serve together with parmesan if using.',
    ],
  },
  {
    id: 'chicken-leek-pie',
    name: 'Chicken & leek pie',
    description: 'A creamy filling under a puff pastry lid.',
    cuisine: 'british',
    mainProtein: 'chicken',
    effortMinutes: 60,
    costBand: 'mid',
    tags: ['mild'],
    ingredients: [
      item('chicken-thighs', 130, 'g', 'diced'),
      item('leeks', 0.6, 'each', 'sliced'),
      item('puff-pastry', 60, 'g'),
      item('double-cream', 40, 'ml'),
      item('stock-cubes-chicken', 0.3, 'each'),
      item('butter', 8, 'g'),
      item('plain-flour', 8, 'g'),
    ],
    method: [
      'Soften the leeks in the butter.',
      'Add the chicken and brown lightly.',
      'Stir in the flour, then the stock and cream, and simmer until thickened.',
      'Spoon into a dish and top with the pastry.',
      'Bake until the pastry is puffed and golden, about 25 minutes.',
    ],
  },
  {
    id: 'corned-beef-hash',
    name: 'Corned beef hash',
    description: 'A tin-of-corned-beef standby, crisped in the pan.',
    cuisine: 'british',
    mainProtein: 'beef',
    effortMinutes: 35,
    costBand: 'budget',
    tags: ['budget', 'mild'],
    ingredients: [
      item('corned-beef', 100, 'g'),
      item('potatoes', 170, 'g', 'diced'),
      item('onions', 0.4, 'each', 'diced'),
      item('eggs', 1, 'each', 'fried, to serve'),
      item('worcestershire-sauce', 3, 'ml', null, true),
    ],
    method: [
      'Parboil the potatoes.',
      'Fry the onion until soft.',
      'Add the potatoes and corned beef, and fry until crisp in places.',
      'Fry an egg per portion.',
      'Serve the hash topped with the egg.',
    ],
  },
  {
    id: 'jacket-potatoes',
    name: 'Jacket potatoes with beans & cheese',
    description: 'The simplest dinner in the book, and a real favourite.',
    cuisine: 'british',
    mainProtein: 'none',
    effortMinutes: 25,
    costBand: 'budget',
    tags: ['budget', 'quick', 'veggie', 'mild'],
    ingredients: [
      item('potatoes', 260, 'g', '1 large baking potato'),
      item('baked-beans', 120, 'g'),
      item('cheddar', 25, 'g'),
      item('butter', 8, 'g'),
    ],
    method: [
      'Prick the potatoes and microwave or bake until soft through.',
      'Warm the baked beans.',
      'Split the potatoes and fluff the inside with a little butter.',
      'Top with beans and cheddar.',
    ],
  },
  {
    id: 'beans-sausages-toast',
    name: 'Beans, sausages & toast',
    description: 'A fifteen-minute plate for the nights nothing else fits.',
    cuisine: 'british',
    mainProtein: 'pork',
    effortMinutes: 15,
    costBand: 'budget',
    tags: ['budget', 'quick', 'mild'],
    ingredients: [
      item('sausages', 2, 'each'),
      item('baked-beans', 130, 'g'),
      item('bread-white', 2, 'each'),
    ],
    method: [
      'Grill or fry the sausages until browned through.',
      'Warm the beans.',
      'Toast the bread.',
      'Serve together.',
    ],
  },
  {
    id: 'omelette-chips',
    name: 'Omelette & chips',
    description: 'Eggs from the fridge, chips from the freezer, dinner in twenty.',
    cuisine: 'british',
    mainProtein: 'egg',
    effortMinutes: 20,
    costBand: 'budget',
    tags: ['budget', 'quick', 'veggie', 'mild'],
    ingredients: [
      item('eggs', 2, 'each'),
      item('cheddar', 20, 'g', null, true),
      item('chips-frozen', 150, 'g'),
      item('butter', 5, 'g'),
    ],
    method: [
      'Bake or fry the chips.',
      'Beat the eggs and season.',
      'Cook the omelette in the butter, folding in cheddar if using.',
      'Serve with the chips.',
    ],
  },
  {
    id: 'full-english-tea',
    name: 'Full English for tea',
    description: 'A breakfast plate, served for dinner instead.',
    cuisine: 'british',
    mainProtein: 'pork',
    effortMinutes: 25,
    costBand: 'mid',
    tags: ['quick', 'mild'],
    ingredients: [
      item('sausages', 2, 'each'),
      item('bacon', 80, 'g'),
      item('eggs', 1, 'each'),
      item('baked-beans', 100, 'g'),
      item('bread-white', 1.5, 'each'),
      item('mushrooms', 50, 'g', null, true),
      item('fresh-tomatoes', 50, 'g', 'halved and grilled', true),
    ],
    method: [
      'Grill the sausages and bacon.',
      'Fry or grill the mushrooms and tomatoes if using.',
      'Warm the beans.',
      'Fry or poach the eggs.',
      'Toast the bread.',
      'Serve everything together.',
    ],
  },
  {
    id: 'homemade-burgers',
    name: 'Homemade burgers & wedges',
    description: 'Proper beef patties with oven wedges instead of chips.',
    cuisine: 'american',
    mainProtein: 'beef',
    effortMinutes: 35,
    costBand: 'mid',
    tags: ['mild'],
    ingredients: [
      item('beef-mince', 130, 'g'),
      item('burger-buns', 1, 'each'),
      item('potatoes', 180, 'g', 'cut into wedges'),
      item('cheddar', 20, 'g', null, true),
      item('lettuce', 0.1, 'each', null, true),
      item('tomato-ketchup', 10, 'ml', null, true),
    ],
    method: [
      'Cut the potatoes into wedges and roast until golden.',
      'Shape the mince into patties and season.',
      'Fry or grill the patties until cooked through, adding cheese to melt if using.',
      'Toast the buns.',
      'Build the burgers with lettuce and ketchup if using.',
      'Serve with the wedges.',
    ],
  },
  {
    id: 'hot-dogs-corn',
    name: 'Hot dogs & corn on the cob',
    description: 'A twenty-minute plate that feels like a treat.',
    cuisine: 'american',
    mainProtein: 'pork',
    effortMinutes: 20,
    costBand: 'budget',
    tags: ['budget', 'quick', 'mild'],
    ingredients: [
      item('sausages', 2, 'each'),
      item('hot-dog-buns', 2, 'each'),
      item('corn-on-the-cob', 1, 'each'),
      item('tomato-ketchup', 10, 'ml', null, true),
      item('mustard', 5, 'g', null, true),
    ],
    method: [
      'Boil or grill the sausages until cooked through.',
      'Boil the corn until tender.',
      'Warm the buns.',
      'Build the hot dogs with ketchup and mustard if using.',
      'Serve with the corn.',
    ],
  },
  {
    id: 'nuggets-smiles',
    name: 'Chicken nuggets, potato smiles & veg',
    description: 'The children’s favourite, kept firmly in the weekly rotation.',
    cuisine: 'british',
    mainProtein: 'chicken',
    effortMinutes: 25,
    costBand: 'budget',
    tags: ['budget', 'quick', 'mild'],
    ingredients: [
      item('chicken-nuggets', 4, 'each'),
      item('potato-smiles', 120, 'g'),
      item('peas-frozen', 50, 'g'),
    ],
    method: [
      'Bake the nuggets and potato smiles according to pack instructions.',
      'Boil the peas.',
      'Serve together.',
    ],
  },
  {
    id: 'fish-fingers-mash',
    name: 'Fish fingers, mash & beans',
    description: 'A gentle, no-surprises tea for the whole table.',
    cuisine: 'british',
    mainProtein: 'fish',
    effortMinutes: 25,
    costBand: 'budget',
    tags: ['budget', 'quick', 'mild'],
    ingredients: [
      item('fish-fingers', 3, 'each'),
      item('potatoes', 170, 'g'),
      item('baked-beans', 100, 'g'),
      item('butter', 8, 'g'),
      item('milk', 25, 'ml'),
    ],
    method: [
      'Grill or bake the fish fingers.',
      'Boil the potatoes and mash with butter and milk.',
      'Warm the beans.',
      'Serve together.',
    ],
  },
  {
    id: 'lentil-dahl',
    name: 'Lentil dahl & rice',
    description: 'A cheap, gently spiced pot that batches well.',
    cuisine: 'indian',
    mainProtein: 'none',
    effortMinutes: 40,
    costBand: 'budget',
    tags: ['budget', 'batch', 'veggie', 'mild'],
    ingredients: [
      item('lentils-red', 90, 'g'),
      item('onions', 0.4, 'each', 'diced'),
      item('garlic', 0.6, 'each', 'crushed'),
      item('ginger', 4, 'g', 'grated'),
      item('turmeric', 1.5, 'g'),
      item('cumin', 1.5, 'g'),
      item('curry-powder', 1.5, 'g'),
      item('coconut-milk', 50, 'ml', null, true),
      item('rice-basmati', 80, 'g'),
    ],
    method: [
      'Rinse the lentils.',
      'Soften the onion, garlic and ginger with the spices.',
      'Add the lentils and enough water to cover, and simmer 25 minutes until soft.',
      'Stir in coconut milk if using.',
      'Cook the rice.',
      'Serve together.',
    ],
  },
  {
    id: 'veggie-chilli',
    name: 'Veggie chilli',
    description: 'The bean-and-tomato pot that proves veggie nights aren’t a compromise.',
    cuisine: 'mexican',
    mainProtein: 'none',
    effortMinutes: 45,
    costBand: 'budget',
    tags: ['budget', 'batch', 'veggie', 'mild'],
    ingredients: [
      item('kidney-beans', 100, 'g'),
      item('chickpeas', 60, 'g', null, true),
      item('chopped-tomatoes', 120, 'g'),
      item('onions', 0.4, 'each', 'diced'),
      item('garlic', 0.7, 'each', 'crushed'),
      item('peppers', 0.4, 'each', 'diced'),
      item('chilli-powder', 1.5, 'g'),
      item('cumin', 1, 'g'),
      item('tomato-puree', 8, 'g'),
      item('rice-long-grain', 80, 'g'),
    ],
    method: [
      'Soften the onion, pepper and garlic.',
      'Stir in the spices, then the tomatoes and puree.',
      'Add the kidney beans and chickpeas if using, and simmer 25 minutes.',
      'Cook the rice.',
      'Serve the chilli over rice.',
    ],
  },
  {
    id: 'butternut-risotto',
    name: 'Butternut squash risotto',
    description: 'A patient, stir-and-wait dinner with a sweet, creamy result.',
    cuisine: 'italian',
    mainProtein: 'none',
    effortMinutes: 40,
    costBand: 'budget',
    // Not 'veggie': parmesan is traditionally made with animal rennet.
    tags: ['budget', 'mild'],
    ingredients: [
      item('rice-risotto', 85, 'g'),
      item('butternut-squash', 0.3, 'each', 'diced'),
      item('onions', 0.4, 'each', 'diced'),
      item('garlic', 0.5, 'each', 'crushed'),
      item('stock-cubes-vegetable', 0.5, 'each'),
      item('parmesan', 20, 'g'),
      item('butter', 10, 'g'),
      item('olive-oil', 5, 'ml'),
    ],
    method: [
      'Roast the squash until tender.',
      'Soften the onion and garlic in the oil.',
      'Add the rice and stir for a minute, then add hot stock a ladle at a time, stirring until absorbed.',
      'Continue until the rice is creamy and just tender, about 20 minutes.',
      'Stir in the roasted squash, parmesan and butter.',
    ],
  },
  {
    id: 'cauliflower-cheese-bacon',
    name: 'Cauliflower cheese with bacon',
    description: 'A cheese-sauce bake with a smoky bacon lift.',
    cuisine: 'british',
    mainProtein: 'pork',
    effortMinutes: 45,
    costBand: 'budget',
    tags: ['budget', 'mild'],
    ingredients: [
      item('cauliflower', 0.3, 'each'),
      item('bacon', 40, 'g'),
      item('cheddar', 35, 'g'),
      item('milk', 70, 'ml'),
      item('butter', 10, 'g'),
      item('plain-flour', 10, 'g'),
    ],
    method: [
      'Boil or steam the cauliflower until just tender.',
      'Fry the bacon until crisp.',
      'Make a white sauce with the butter, flour and milk, then melt in most of the cheddar.',
      'Combine the cauliflower and bacon with the sauce.',
      'Top with the remaining cheddar and grill until golden.',
    ],
  },
  {
    id: 'minestrone-soup',
    name: 'Minestrone soup & crusty bread',
    description: 'A vegetable soup thickened with a handful of small pasta.',
    cuisine: 'italian',
    mainProtein: 'none',
    effortMinutes: 35,
    costBand: 'budget',
    tags: ['budget', 'batch', 'veggie', 'mild'],
    ingredients: [
      item('kidney-beans', 70, 'g'),
      item('carrots', 35, 'g', 'diced'),
      item('celery', 0.4, 'each', 'diced'),
      item('onions', 0.3, 'each', 'diced'),
      item('chopped-tomatoes', 120, 'g'),
      item('stock-cubes-vegetable', 0.5, 'each'),
      item('pasta-fusilli', 40, 'g'),
      item('baguette', 0.25, 'each'),
    ],
    method: [
      'Soften the onion, carrot and celery.',
      'Add the tomatoes, stock and beans, and simmer 15 minutes.',
      'Add the pasta and cook until tender, about 10 minutes.',
      'Season and serve with the crusty bread.',
    ],
  },
  {
    id: 'chicken-noodle-soup',
    name: 'Chicken noodle soup',
    description: 'A restorative, low-effort pot for a cold week.',
    cuisine: 'chinese',
    mainProtein: 'chicken',
    effortMinutes: 35,
    costBand: 'budget',
    tags: ['budget', 'batch', 'mild'],
    ingredients: [
      item('chicken-breast', 130, 'g', 'shredded'),
      item('noodles-egg', 70, 'g'),
      item('carrots', 30, 'g', 'sliced'),
      item('celery', 0.3, 'each', 'sliced'),
      item('stock-cubes-chicken', 0.6, 'each'),
      item('spring-onions', 1, 'each', null, true),
    ],
    method: [
      'Poach the chicken in stock until cooked through, then shred.',
      'Add the carrot and celery to the stock and simmer until tender.',
      'Add the noodles and cook until soft.',
      'Return the chicken to warm through.',
      'Scatter with spring onion if using.',
    ],
  },
  {
    id: 'leek-potato-soup',
    name: 'Leek & potato soup',
    description: 'A smooth, blended soup from four cheap ingredients.',
    cuisine: 'british',
    mainProtein: 'none',
    effortMinutes: 30,
    costBand: 'budget',
    tags: ['budget', 'batch', 'veggie', 'mild', 'freezer'],
    ingredients: [
      item('leeks', 0.7, 'each', 'sliced'),
      item('potatoes', 150, 'g', 'diced'),
      item('onions', 0.3, 'each', 'diced'),
      item('stock-cubes-vegetable', 0.5, 'each'),
      item('butter', 10, 'g'),
      item('double-cream', 20, 'ml', null, true),
    ],
    method: [
      'Soften the leeks and onion in the butter.',
      'Add the potatoes and stock, and simmer until the potatoes are soft, about 20 minutes.',
      'Blend until smooth.',
      'Stir through cream if using, and serve.',
    ],
  },
  {
    id: 'gammon-egg-chips',
    name: 'Gammon, egg & chips',
    description: 'A pub-style plate that takes half the time to cook at home.',
    cuisine: 'british',
    mainProtein: 'pork',
    effortMinutes: 35,
    costBand: 'mid',
    tags: ['mild'],
    ingredients: [
      item('gammon', 150, 'g'),
      item('eggs', 1, 'each'),
      item('chips-frozen', 150, 'g'),
    ],
    method: [
      'Bake the chips.',
      'Grill or fry the gammon until cooked through.',
      'Fry an egg per portion.',
      'Serve together.',
    ],
  },
  {
    id: 'pork-chops-mash',
    name: 'Pork chops & mash',
    description: 'A straightforward chop-and-mash dinner with a splash of gravy.',
    cuisine: 'british',
    mainProtein: 'pork',
    effortMinutes: 40,
    costBand: 'mid',
    tags: ['mild'],
    ingredients: [
      item('pork-chops', 1, 'each'),
      item('potatoes', 180, 'g'),
      item('green-beans', 60, 'g'),
      item('butter', 10, 'g'),
      item('milk', 30, 'ml'),
      item('gravy-granules', 8, 'g', null, true),
    ],
    method: [
      'Boil the potatoes and mash with butter and milk.',
      'Fry or grill the pork chops until cooked through.',
      'Steam the green beans.',
      'Make gravy if using.',
      'Serve together.',
    ],
  },
  {
    id: 'roast-pork-crackling',
    name: 'Roast pork with crackling',
    description: 'A slow-roasted joint with crisp crackling, worth the long oven time.',
    cuisine: 'british',
    mainProtein: 'pork',
    effortMinutes: 100,
    costBand: 'mid',
    tags: ['mild'],
    ingredients: [
      item('pork-joint', 170, 'g'),
      item('potatoes', 200, 'g', 'for roasting'),
      item('carrots', 40, 'g'),
      item('green-beans', 50, 'g'),
      item('gravy-granules', 12, 'g'),
      item('olive-oil', 5, 'ml'),
    ],
    method: [
      'Score the pork skin and rub with oil and salt.',
      'Roast slowly until the meat is tender and the crackling is crisp.',
      'Parboil then roast the potatoes alongside.',
      'Steam the carrots and beans.',
      'Make gravy from the resting juices.',
      'Carve and serve with the crackling, vegetables and gravy.',
    ],
  },
  {
    id: 'roast-beef-dinner',
    name: 'Roast beef dinner',
    description: 'A treat-night roast with Yorkshire puddings from scratch.',
    cuisine: 'british',
    mainProtein: 'beef',
    effortMinutes: 110,
    costBand: 'treat',
    tags: ['mild'],
    ingredients: [
      item('beef-joint', 170, 'g'),
      item('potatoes', 200, 'g', 'for roasting'),
      item('carrots', 40, 'g'),
      item('green-beans', 50, 'g'),
      item('plain-flour', 30, 'g', 'yorkshire batter'),
      item('eggs', 0.4, 'each', 'yorkshire batter'),
      item('milk', 45, 'ml', 'yorkshire batter'),
      item('gravy-granules', 12, 'g'),
    ],
    method: [
      'Roast the beef to taste, then rest well before carving.',
      'Whisk the flour, egg and milk into a batter and rest.',
      'Parboil then roast the potatoes.',
      'Pour the batter into hot fat in a tin and bake until risen for the Yorkshire puddings.',
      'Steam the carrots and beans.',
      'Make gravy from the resting juices, carve and serve.',
    ],
  },
  {
    id: 'chicken-chorizo-paella',
    name: 'Chicken & chorizo paella',
    description: 'A one-pan rice dinner with a smoky chorizo edge.',
    cuisine: 'spanish',
    mainProtein: 'chicken',
    effortMinutes: 45,
    costBand: 'mid',
    tags: ['mild'],
    ingredients: [
      item('chicken-thighs', 110, 'g', 'diced'),
      item('chorizo', 30, 'g', 'sliced'),
      item('rice-long-grain', 85, 'g'),
      item('peppers', 0.4, 'each', 'sliced'),
      item('peas-frozen', 40, 'g'),
      item('stock-cubes-chicken', 0.5, 'each'),
      item('smoked-paprika', 2, 'g'),
    ],
    method: [
      'Brown the chicken and chorizo together.',
      'Add the peppers and rice, and stir to coat in the fat.',
      'Add hot stock and paprika, and simmer without stirring until the rice is tender.',
      'Stir through the peas for the last few minutes.',
      'Rest briefly, then serve.',
    ],
  },
  {
    id: 'spanish-tortilla',
    name: 'Spanish tortilla',
    description: 'A thick potato omelette, good hot or at room temperature.',
    cuisine: 'spanish',
    mainProtein: 'egg',
    effortMinutes: 35,
    costBand: 'budget',
    tags: ['budget', 'veggie', 'mild'],
    ingredients: [
      item('potatoes', 160, 'g', 'thinly sliced'),
      item('eggs', 1.5, 'each'),
      item('onions', 0.4, 'each', 'sliced'),
      item('olive-oil', 10, 'ml'),
    ],
    method: [
      'Slowly fry the potatoes and onion in the oil until soft.',
      'Beat the eggs and season.',
      'Combine the potatoes and onion with the egg.',
      'Cook slowly in a pan until mostly set, then flip to finish the other side.',
      'Rest and slice to serve.',
    ],
  },
  {
    id: 'greek-chicken-potatoes',
    name: 'Greek chicken & potatoes',
    description: 'A lemon-and-oregano tray bake with almost no washing up.',
    cuisine: 'greek',
    mainProtein: 'chicken',
    effortMinutes: 60,
    costBand: 'mid',
    tags: ['mild'],
    ingredients: [
      item('chicken-thighs', 140, 'g'),
      item('potatoes', 180, 'g', 'cut into wedges'),
      item('lemons', 0.3, 'each'),
      item('oregano', 1.5, 'g'),
      item('olive-oil', 8, 'ml'),
      item('garlic', 0.6, 'each', 'crushed'),
    ],
    method: [
      'Toss the chicken and potatoes with the oil, lemon, oregano and garlic.',
      'Roast until the chicken is cooked through and the potatoes are golden, about 45 minutes.',
      'Turn everything halfway through.',
      'Serve together with the pan juices.',
    ],
  },
  {
    id: 'moussaka',
    name: 'Moussaka',
    description: 'A layered lamb and aubergine bake with a set custard top.',
    cuisine: 'greek',
    mainProtein: 'lamb',
    effortMinutes: 75,
    costBand: 'mid',
    tags: ['batch'],
    ingredients: [
      item('lamb-mince', 120, 'g'),
      item('aubergine', 0.5, 'each', 'sliced'),
      item('potatoes', 100, 'g', 'sliced'),
      item('chopped-tomatoes', 120, 'g'),
      item('onions', 0.4, 'each', 'diced'),
      item('garlic', 0.6, 'each', 'crushed'),
      item('cinnamon', 1, 'g'),
      item('milk', 60, 'ml'),
      item('plain-flour', 10, 'g'),
      item('butter', 10, 'g'),
      item('eggs', 0.3, 'each', 'bechamel'),
    ],
    method: [
      'Salt and fry the aubergine and potato slices until golden.',
      'Soften the onion and garlic, brown the lamb, then add the tomatoes and cinnamon and simmer 20 minutes.',
      'Make a white sauce with the butter, flour and milk, then beat in the egg.',
      'Layer the aubergine, potato and lamb sauce in a dish.',
      'Top with the white sauce.',
      'Bake until golden, about 30 minutes.',
    ],
  },
  {
    id: 'beef-stroganoff',
    name: 'Beef stroganoff',
    description: 'Quick-fried beef strips in a mustardy soured-cream sauce.',
    cuisine: 'russian',
    mainProtein: 'beef',
    effortMinutes: 40,
    costBand: 'mid',
    tags: ['mild'],
    ingredients: [
      item('beef-steak', 96, 'g', 'sliced into strips'),
      item('mushrooms', 70, 'g', 'sliced'),
      item('onions', 0.4, 'each', 'sliced'),
      item('creme-fraiche', 50, 'g'),
      item('mustard', 4, 'g'),
      item('rice-long-grain', 80, 'g'),
    ],
    method: [
      'Fry the beef strips quickly over high heat, then set aside.',
      'Soften the onion and mushrooms in the same pan.',
      'Stir in the mustard and crème fraîche, and warm through.',
      'Return the beef and heat gently without boiling.',
      'Cook the rice.',
      'Serve together.',
    ],
  },
  {
    id: 'chicken-bacon-pasta-bake',
    name: 'Chicken & bacon pasta bake',
    description: 'A creamy pasta bake that reheats for lunch the next day.',
    cuisine: 'italian',
    mainProtein: 'chicken',
    effortMinutes: 40,
    costBand: 'mid',
    tags: ['batch', 'mild', 'freezer'],
    ingredients: [
      item('chicken-breast', 120, 'g', 'diced'),
      item('bacon', 35, 'g', 'diced'),
      item('pasta-penne', 85, 'g'),
      item('double-cream', 50, 'ml'),
      item('cheddar', 25, 'g'),
      item('mushrooms', 50, 'g', null, true),
    ],
    method: [
      'Cook the pasta.',
      'Fry the chicken and bacon until cooked through.',
      'Add mushrooms if using, and the cream, and simmer briefly.',
      'Combine with the pasta and top with cheddar.',
      'Bake until golden and bubbling.',
    ],
  },
  {
    id: 'salmon-new-potatoes',
    name: 'Salmon, new potatoes & greens',
    description: 'A treat-night plate that is on the table in half an hour.',
    cuisine: 'british',
    mainProtein: 'fish',
    effortMinutes: 30,
    costBand: 'treat',
    tags: ['quick'],
    ingredients: [
      item('salmon-fillets', 1, 'each'),
      item('potatoes', 180, 'g', 'new potatoes'),
      item('broccoli', 70, 'g'),
      item('butter', 8, 'g'),
      item('lemons', 0.2, 'each', null, true),
    ],
    method: [
      'Boil the new potatoes until tender.',
      'Steam the broccoli.',
      'Pan-fry or bake the salmon until just cooked through.',
      'Toss the potatoes in butter.',
      'Serve together with a squeeze of lemon if using.',
    ],
  },
  {
    id: 'cod-parsley-sauce',
    name: 'Cod in parsley sauce with mash',
    description: 'A gentle, old-fashioned fish supper.',
    cuisine: 'british',
    mainProtein: 'fish',
    effortMinutes: 35,
    costBand: 'mid',
    tags: ['mild'],
    ingredients: [
      item('cod-fillets', 1, 'each'),
      item('potatoes', 180, 'g'),
      item('milk', 80, 'ml'),
      item('butter', 12, 'g'),
      item('plain-flour', 10, 'g'),
      item('fresh-parsley', 2, 'g', 'chopped'),
    ],
    method: [
      'Poach the cod gently in the milk.',
      'Boil the potatoes and mash with a little butter and milk.',
      'Make a white sauce from the poaching milk, butter and flour.',
      'Stir the chopped parsley through the sauce.',
      'Serve the cod and sauce over the mash.',
    ],
  },
  {
    id: 'prawn-linguine',
    name: 'Prawn linguine',
    description: 'A treat-night pasta, garlicky and quick.',
    cuisine: 'italian',
    mainProtein: 'prawns',
    effortMinutes: 25,
    costBand: 'treat',
    tags: ['quick'],
    ingredients: [
      item('prawns', 100, 'g'),
      item('pasta-spaghetti', 90, 'g'),
      item('garlic', 0.7, 'each', 'crushed'),
      item('olive-oil', 10, 'ml'),
      item('chilli-powder', 1, 'g', null, true),
      item('lemons', 0.2, 'each', null, true),
      item('fresh-parsley', 2, 'g', null, true),
    ],
    method: [
      'Cook the pasta.',
      'Gently fry the garlic and chilli in the oil if using.',
      'Add the prawns and cook until pink.',
      'Toss with the drained pasta and a squeeze of lemon.',
      'Scatter with parsley if using.',
    ],
  },
  {
    id: 'turkey-mince-spaghetti',
    name: 'Turkey mince spaghetti',
    description: 'A lighter take on the bolognese, just as fast.',
    cuisine: 'italian',
    mainProtein: 'turkey',
    effortMinutes: 30,
    costBand: 'budget',
    tags: ['budget', 'quick', 'mild'],
    ingredients: [
      item('turkey-mince', 110, 'g'),
      item('pasta-spaghetti', 85, 'g'),
      item('chopped-tomatoes', 120, 'g'),
      item('onions', 0.4, 'each', 'diced'),
      item('garlic', 0.6, 'each', 'crushed'),
      item('carrots', 30, 'g', 'grated'),
      item('tomato-puree', 8, 'g'),
      item('mixed-herbs', 1, 'g'),
    ],
    method: [
      'Soften the onion, carrot and garlic.',
      'Brown the turkey mince.',
      'Stir in the tomatoes, puree and herbs, and simmer 15 minutes.',
      'Cook the spaghetti.',
      'Serve together.',
    ],
  },
  {
    id: 'kedgeree',
    name: 'Kedgeree',
    description: 'A curried rice and smoked fish dinner, quicker than it sounds.',
    cuisine: 'british',
    mainProtein: 'fish',
    effortMinutes: 35,
    costBand: 'mid',
    tags: ['mild'],
    ingredients: [
      item('smoked-haddock', 96, 'g'),
      item('rice-basmati', 85, 'g'),
      item('eggs', 1, 'each', 'boiled'),
      item('curry-powder', 2, 'g'),
      item('butter', 10, 'g'),
      item('fresh-parsley', 2, 'g', null, true),
    ],
    method: [
      'Poach the smoked haddock in water or milk, then flake.',
      'Boil the eggs, then quarter.',
      'Cook the rice with the curry powder.',
      'Fold the flaked fish through the rice.',
      'Top with the egg, and parsley if using.',
    ],
  },
];
